import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';

import { environment } from 'src/environments/environment';
import { PlaceDetail } from '../../../services/locations.service';
import { SuburbCatalogueService } from './workspace/catalogue.service';
import { ContentRecordsService } from './workspace/content-records.service';
import { EditorHostService } from './workspace/editor-host.service';
import { MapEditsService } from './workspace/map-edits.service';
import { MapHostService } from './workspace/map-host.service';
import { MapToolsService } from './workspace/map-tools.service';
import { PageEditorService } from './workspace/page-editor.service';
import { PagePublisherService } from './workspace/page-publisher.service';
import { PageTabService } from './workspace/page-tab.service';
import { PageTemplatesService } from './workspace/page-templates.service';
import { PanelNavService } from './workspace/panel-nav.service';
import { ReadinessViewService } from './workspace/readiness-view.service';
import { SubRegionsService } from './workspace/subregions.service';
import { SuburbDetailService } from './workspace/suburb-detail.service';
import { StagePreviewService } from './workspace/stage-preview.service';
import { SuburbPlacesService } from './workspace/suburb-places.service';
import { KeyCommand, keyCommand } from './workspace/workspace-keys';
import { WorkspaceStateService } from './workspace/workspace-state.service';
import { suburbMapTestHandle } from './workspace/workspace-test-handle';

/** The dev-build handle the Playwright specs read (workspace-test-handle.ts). */
interface TestWindow {
  __suburbMap?: { engine: unknown; component: object };
}

/**
 * Suburbs (#256; plan §6, decided with Ivan 2026-10-15): one popup for everything about suburbs, like the other
 * content editors. A map of every ABS suburb and the regions (assign, erase, paint, lasso, grow, checks), a List view
 * of the same suburbs with their crawl data, a Readiness view of every suburb page (#262), and a side panel that slides
 * in for one suburb (overview, places, crawl data, our own notes, its website page, drafts), for regions and
 * sub-regions, for the pages that aren't one suburb, and for the page templates. It replaces the old Suburbs list.
 *
 * This component is the shell: it lays out the tool column, the stage (map or list) and the side panel, provides the
 * workspace's services (one set per open popup), starts the map and turns key presses into commands. Everything else
 * lives in the services (workspace/) and the components they back. README.md in this folder maps it.
 */
@Component({
  selector: 'app-content-suburb-workspace',
  templateUrl: './content-suburb-workspace.component.html',
  styleUrls: ['./content-suburb-workspace.component.scss'],
  providers: [
    WorkspaceStateService,
    SuburbCatalogueService,
    MapEditsService,
    ContentRecordsService,
    SuburbDetailService,
    SuburbPlacesService,
    SubRegionsService,
    EditorHostService,
    PagePublisherService,
    PageEditorService,
    PanelNavService,
    MapToolsService,
    MapHostService,
    // #262: the Page tab's reads and writes, the page templates, the Readiness view.
    PageTabService,
    PageTemplatesService,
    ReadinessViewService,
    // #263: the block preview on the stage.
    StagePreviewService,
  ],
})
export class ContentSuburbWorkspaceComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('root', { static: true }) root!: ElementRef<HTMLDivElement>;
  @ViewChild('mapEl', { static: true }) mapEl!: ElementRef<HTMLDivElement>;
  @ViewChild('lassoEl', { static: true }) lassoEl!: ElementRef<HTMLCanvasElement>;

  private destroyed = false;

  constructor(
    public state: WorkspaceStateService,
    public catalogue: SuburbCatalogueService,
    public places: SuburbPlacesService,
    public nav: PanelNavService,
    public records: ContentRecordsService,
    public stagePreview: StagePreviewService,
    private config: DynamicDialogConfig,
    private editor: EditorHostService,
    private edits: MapEditsService,
    private tools: MapToolsService,
    private mapHost: MapHostService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.state.bindRedraw(() => {
      if (!this.destroyed) this.cdr.detectChanges();
    });
    this.mapHost.attach(this.mapEl.nativeElement, this.lassoEl.nativeElement);
    if (!environment.production) {
      const { zone, state, catalogue, places, records, tools, nav } = this;
      (window as TestWindow).__suburbMap = suburbMapTestHandle({
        zone,
        state,
        catalogue,
        places,
        records,
        tools,
        nav,
      });
    }
    this.places.loadPendingCount();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.mapHost.start(this.config?.data?.focus);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.state.unbindRedraw();
    this.state.engine?.destroy();
    const w = window as TestWindow;
    if (w.__suburbMap?.engine === this.state.engine) delete w.__suburbMap;
  }

  /** The place panel or the map popup changed a place. */
  onPlaceChanged(d: PlaceDetail): void {
    this.places.placeChanged(d);
  }

  /** The popup's "Details": the full place panel. */
  popupDetails(id: number): void {
    this.places.closePopup();
    this.nav.openPlace(id);
  }

  // ------------------------------------------------------------------ keys (workspace-keys.ts)

  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    const command = this.commandFor(e);
    if (command) this.run(command, e);
    this.state.redraw();
  }

  /** Only keys aimed at this popup (its header too: the dialog moves focus to its close button). */
  private commandFor(e: KeyboardEvent): KeyCommand | null {
    const state = this.state;
    if (!state.engine || state.loading || state.confirm) return null;
    const target = e.target as HTMLElement | null;
    const dialog = this.root.nativeElement.closest('.p-dialog') ?? this.root.nativeElement;
    if (target && target !== document.body && !dialog.contains(target)) return null;
    // Typing: inputs, text areas, and anything in the side panel's body (the editors).
    const typing =
      !!target &&
      (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) ||
        target.isContentEditable ||
        !!target.closest('.side-panel .panel-body'));
    return keyCommand(e, {
      typing,
      onMap: state.view === 'map',
      mode: state.mode,
      regionCount: this.catalogue.regions.length,
    });
  }

  private run(command: KeyCommand, e: KeyboardEvent): void {
    const state = this.state;
    switch (command.kind) {
      case 'escape':
        // A lasso in progress takes Esc (the engine cancels it); a mounted editor keeps the panel open.
        if (!state.engine.drawing && state.panel && !this.editor.open) this.nav.closePanel();
        return;
      case 'undo':
        e.preventDefault();
        void this.edits.undo();
        return;
      case 'region':
        return this.tools.pickRegion(command.index);
      case 'mode':
        state.mode = command.mode;
        return;
      case 'subregion-action':
        return this.tools.toggleSubRegionAction();
      case 'filter':
        return state.setFilter(command.filter, !state.isFilterOn(command.filter));
      case 'grow':
        return this.tools.grow();
      case 'tool':
        state.tool = command.tool;
        return;
    }
  }
}
